import JsSIP from 'jssip';
import { nanoid } from 'nanoid';
import ExtensionResource from 'flow/api/extensions';
import ProvisioningResource from 'flow/api/provisioning';

import throttle from 'lodash/throttle';

import appConfig from 'app-config';
import Actions from 'flow/actions';
import {
    hsIncomingCall,
    hsOutgoingCall,
    hsEndCall,
    hsConfirmCall,
    hsHoldCall,
    hsUnholdCall,
    hsMute,
    hsUnmute,
    hsMultipleCalls,
    hsEndLastCall,
    logAudioDeviceDataIfAppropriate,
} from 'flow/actions/headset-actions';
import { decryptSipInfo } from 'flow/utils/transcode-utils';

import {
    rtcSessionRepresentsCentionErrand,
    getCentionDataFromRtcSession,
} from 'flow/utils/omnichannel-utils';
import {
    rtcSessionRepresentsTicketNonVoice,
    getTicketDataFromRtcSessionRequest,
} from 'flow/utils/ticket-utils';
import {
    OMNICHANNEL_PROPERTIES,
    routeUserToOmnichannelErrand,
    onNewOmnichannelSession,
} from 'flow/actions/omnichannel-actions';
import metricsClient from 'flow/api-clients/metrics-client';
import { setActiveMeetingCallIdAction } from 'flow/actions/meeting-actions';
// Circular with companion-actions (it imports answerCall/startLocalCall from here). Safe because
// neither binding is touched at module evaluation - both are used inside thunk bodies at call time.
import {
    answerCompanionCall,
    callOnCompanion,
    declineCompanionCall,
    muteCompanion,
    publishCompanionMuteState,
    sendCompanionDtmf,
} from 'flow/companion/companion-actions';
import {
    canRelayCallToCompanion,
    isCompanionSignallingSurface,
    selectCompanionAnswersCalls,
    shouldRelayToCompanion,
} from 'flow/companion/companion-utils';
import { selectDesignatedCompanionDevice } from 'flow/selectors/app-selectors';
import {
    addPhoneLogEntry,
    getPlayRingtoneSetting,
    getSoftphoneShouldBeRegistered,
    savePlayRingtoneSetting,
    saveSoftphoneShouldBeRegistered,
    addDeviceLogEntry,
    addUserPhoneLogEntry,
    errorMessageIsRTCPeerConnectionGone,
    addGeneralLogEntry,
    getVideoConferenceWithSip,
    addSipConnectionEstablishedDiv,
    getBlindTransferSetting,
    addOmniChannelLogEntry,
    addTicketLogEntry,
} from 'flow/utils/browser-utils';

import { getCountryCode } from 'flow/utils/extension-utils';
import { getCurrentDateMinusOneDayForSimlog } from 'flow/utils/date-time-utils';
import {
    calculateBackoffTime,
    MAX_BACKOFF_MS,
    parseSipSockets,
} from 'flow/utils/socket-utils';
import {
    networkErrorDispatcher,
    setMicPermission,
    setSnackbar,
} from 'flow/actions/app-actions';
import {
    AttendedTransferState,
    CallMethodType,
    callShouldBeAnsweredAutomatically,
    CallTransferType,
    getUserAgentName,
    PlayRingtoneSetting,
    removeExtraCharacters,
    senderSupportsDtmf,
    NewCallBlockReason,
    blocksOnlyOrigination,
    sessionHasAudioTracks,
    filterOutTCPCandidates,
    filterOutCryptoLineInSDP,
    BLIND_TRANSFER_SETTINGS,
    filterOutPaiHeader,
} from 'flow/utils/call-utils';
import {
    getLocalCallIds,
    getLocalCallSessions,
    getPhoneIsMuted,
    getFocusedLocalCallId,
    getLocalSessionFromCallId,
    hasActiveCallsBlockingNewCall,
    getFocusedSession,
    getFocusedActiveCall,
    getOwnActiveCallByCallId,
    getNumLocalCalls,
    getPaiHeaderFromLocalCall,
    getPendingTransferCallKey,
    getPendingTransferNumber,
    TREAT_CENTION_ERRANDS_LIKE_PHONE_CALLS_EXPERIMENTAL_FEATURE,
    SHOULD_DELAY_REMOVAL_OF_SESSION_OF_TYPE_CENTION_ERRAND,
    selectSoftphoneIsRegistered,
} from 'flow/selectors/call-selectors';
import { ToneGenerator } from 'resources/tone-generator';
import {
    getAutoAnswerWithIpPhone,
    getCallWaitingEnabled,
    getOwnExtensionKey,
    getOwnSipUserInfo,
} from 'flow/selectors/selectors';
import { toggleCall, setPinCode } from 'flow/actions/extension-actions';

import {
    ensureFreshGeoLocation,
    fetchGeoRestrictionEnabled,
    reportOutsideRegionToSurfaces,
    resolveGeoLocation,
    setGeoOutsideFence,
} from 'flow/actions/geolocation-actions';
import { selectCallingNeedsCompanion } from 'flow/selectors/companion-selectors';
import {
    selectPositionIsUnavailable,
    selectGeoLocationCoordinates,
    selectGeoLocationIsReported,
} from 'flow/selectors/geolocation-selectors';
import {
    GEO_FIX_WAIT_MS,
    geoLocationExtraHeader,
    geoRestrictionIsDeployed,
    onGeoLocationPermissionGranted,
} from 'flow/utils/geolocation-utils';
import {
    permissionToMicrophoneIsGranted,
    getMicrophonePermissionState,
    getConnectedMediaDeviceOfKindWithId,
    getMediaConstraints,
    setOutputDeviceWithIdToElement,
    permissionTypes,
} from 'flow/utils/media-utils';
import {
    applyNoiseSuppression,
    releaseNoiseSuppression,
} from 'flow/actions/noise-suppression-actions';
import { putMeetingOnHold } from 'flow/actions/meeting-actions';
import { history } from 'flow/configure-store';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import { spawnNotificationNewTicket } from 'flow/actions/tickets-action';
import {
    selectMeetingIsActive,
    selectMeetingIsInFocus,
} from 'flow/selectors/meeting-selectors';
import { segmentTrack } from 'flow/actions/segment-actions';
import { EVENT_STRINGS } from 'flow/utils/segment-utils';
import { RingTonePlayer } from 'resources/ringtone-player';
import { getFaro } from 'flow/api-clients/faro-client';
import { homeActionUrlPath } from 'flow/utils';
import { selectNotification } from 'flow/selectors/app-selectors';
import { IS_UAE_HOST, IS_UK_HOST } from 'flow/utils/browser-utils-TW';
import {
    FALLBACK_SIP_PROXIES,
    FALLBACK_SIP_PROXIES_UAE,
    FALLBACK_SIP_PROXIES_UK,
} from 'flow/constants/sip-proxy-constants';

const BUSY_TERMINATE_OPTIONS = {
    status_code: '486',
    reason_phrase: 'Busy Here',
};

export const COMPANION_ANSWER_FALLBACK_MS = 3000;

const isRingingIncomingCall = (session) =>
    session.direction === 'incoming' &&
    session.isInProgress() &&
    !session.isEstablished() &&
    !rtcSessionRepresentsTicketNonVoice(session) &&
    !rtcSessionRepresentsCentionErrand(session);

const UPDATE_PROXIES_THROTTLE_MS = 2 * 60 * 1000;

const SIP_CLUSTER = IS_UAE_HOST
    ? 'sip.tvx.etisalat.ae'
    : IS_UK_HOST
      ? 'sip.telavox.co.uk'
      : 'sip.telavox.se';

export let SIP_PROXIES = IS_UAE_HOST
    ? FALLBACK_SIP_PROXIES_UAE
    : IS_UK_HOST
      ? FALLBACK_SIP_PROXIES_UK
      : FALLBACK_SIP_PROXIES;

export let SIP_PROXIES_FROM_PROVISIONING = false;

let isStarted = false;
let registerUserAgentOnCallEnded = false;
let sipOptionsIntervalId = null;

const catchJssipDebugLogs = () => {
    const printInConsole = !appConfig.production;
    if (!JsSIP._default_debug) {
        JsSIP._default_debug = JsSIP.debug.log;
    }
    JsSIP.debug.log = (...args) => {
        if (printInConsole) {
            JsSIP._default_debug(...args);
        }
        const logString = args[0].replace(/%c/g, ''); // Remove coloring syntax used by debug tool.
        addPhoneLogEntry(logString);
    };
};

const calculateStatsOverTime = (stats, firstStatEntry) => {
    let totalOutgoingAudioLevel = 0;
    let totalRoundTripTime = 0;
    let totalIncomingAudioLevel = 0;
    let totalRemoteRoundTripTime = 0;
    const lastStatEntry = stats.length - 1;
    const numberOfStatEntries = lastStatEntry - firstStatEntry + 1;
    for (let i = firstStatEntry; i <= lastStatEntry; i++) {
        totalOutgoingAudioLevel += stats[i].outgoingAudioLevel;
        totalRoundTripTime += stats[i].currentRoundTripTime;
        totalIncomingAudioLevel = stats[i].incomingAudioLevel;
        totalRemoteRoundTripTime = stats[i].remoteRoundTripTime;
    }
    const firstStat = stats[firstStatEntry];
    const lastStat = stats[lastStatEntry];
    const jitterBufferDelay =
        lastStat.jitterBufferDelay - firstStat.jitterBufferDelay;
    const jitterBufferEmittedCount =
        lastStat.jitterBufferEmittedCount - firstStat.jitterBufferEmittedCount;
    const secondsBetweenEntries =
        (lastStat.timestamp - firstStat.timestamp) / 1000;
    const jitterPerSecond =
        (lastStat.jitter - firstStat.jitter) / secondsBetweenEntries;
    const remoteJitterPerSecond =
        (lastStat.remoteJitter - firstStat.remoteJitter) /
        secondsBetweenEntries;

    const candidatePairs = {};
    for (const [id, pair] of Object.entries(lastStat.candidatePairs)) {
        const prevPair =
            'candidatePairs' in firstStat ? firstStat.candidatePairs[id] : null;
        const pairStats = {
            requestsSent:
                pair.requestsSent - (prevPair ? prevPair.requestsSent : 0),
            consentRequestsSent:
                pair.consentRequestsSent -
                (prevPair ? prevPair.consentRequestsSent : 0),
            responsesReceived:
                pair.responsesReceived -
                (prevPair ? prevPair.responsesReceived : 0),
            currentRoundTripTime: pair.currentRoundTripTime,
            state: pair.state,
            remote: `${pair.remoteIp}:${pair.remotePort}`,
            local: `${pair.localIp}:${pair.localPort}`,
        };
        candidatePairs[id] = pairStats;
    }

    return {
        averageOutgoingAudioLevel:
            totalOutgoingAudioLevel / numberOfStatEntries,
        averageIncomingAudioLevel:
            totalIncomingAudioLevel / numberOfStatEntries,
        averageRoundTripTime: totalRoundTripTime / numberOfStatEntries,
        packetsReceived: lastStat.packetsReceived - firstStat.packetsReceived,
        packetsLost: lastStat.packetsLost - firstStat.packetsLost,
        jitterPerSecond,
        averageJitterBufferDelay: jitterBufferDelay / jitterBufferEmittedCount,
        packetsSent: lastStat.packetsSent - firstStat.packetsSent,
        retransmittedPacketsSent:
            lastStat.retransmittedPacketsSent -
            firstStat.retransmittedPacketsSent,
        remoteAverageRoundTripTime:
            totalRemoteRoundTripTime / numberOfStatEntries,
        remotePacketsLost:
            lastStat.remotePacketsLost - firstStat.remotePacketsLost,
        remoteJitterPerSecond,
        candidatePairs,
    };
};

export const updateSipProxies = () => {
    return new Promise((resolve) => {
        ProvisioningResource.getSipServers({
            onResult(json) {
                // Update our list with the successful response
                SIP_PROXIES = parseSipSockets(json);
                SIP_PROXIES_FROM_PROVISIONING =
                    (SIP_PROXIES?.size ?? SIP_PROXIES?.length ?? 0) > 0;
                resolve();
            },
            // If the request gets an error, we want to keep running
            // The SIP_PROXIES variable will have it's old values
            onError: resolve,
            onClientError: resolve,
        });
    });
};

export const updateSipProxiesAsGuest = () => {
    return new Promise((resolve) => {
        ProvisioningResource.getSipServersAsGuest({
            onResult(json) {
                SIP_PROXIES = parseSipSockets(json);
                resolve();
            },
            // If the request gets an error, we want to keep running
            // The SIP_PROXIES variable will have it's old values
            onError: resolve,
            onClientError: resolve,
        });
    });
};

const updateSipProxiesThrottled = throttle(
    updateSipProxies,
    UPDATE_PROXIES_THROTTLE_MS,
    { trailing: false },
);

export const configureJsSIPLogs = () => {
    JsSIP.debug.enable('JsSIP:*');
    catchJssipDebugLogs();
};

const sendCallEndedMetric = (data, session) => {
    const cause = Object.keys(JsSIP.C.causes).find(
        (key) => JsSIP.C.causes[key] === data.cause,
    );
    const path = [session.direction, data.originator, cause];
    metricsClient.sendCallEndedMetric(path);
};

const onSessionEndedOrFailed = (data, session) => (dispatch, getState) => {
    const state = getState();
    const { phone } = state;
    clearInterval(session.data.webrtcStatsTimer);
    clearInterval(session.data.rtpPollingIntervalForDeterminingProgressTones);

    releaseNoiseSuppression(session);
    sendCallEndedMetric(data, session);

    dispatch(
        hsEndCall(session.data['callId'], session.data.hungupThroughHeadset),
    );

    const numLocalCalls = getNumLocalCalls(getState());
    const paiHeader = getPaiHeaderFromLocalCall(state);

    if (numLocalCalls === 1) {
        //Not unmuting if session ended is non-call item
        if (!rtcSessionRepresentsTicketNonVoice(session)) {
            // If the last session is about to end, reset the mute state
            dispatch({ type: Actions.PHONE_MUTED, muted: false });
        }

        dispatch(hsEndLastCall());
        ToneGenerator.context.suspend();
    }

    if (registerUserAgentOnCallEnded && numLocalCalls === 1) {
        registerUserAgentOnCallEnded = false;
        const userAgent = phone.get('userAgent');
        userAgent.register();
    }

    if (paiHeader) {
        // Setting a timeout because the call item is still present for about 3-4s after the call has eneded.
        setTimeout(() => {
            dispatch(removePaiHeader());
        }, 5000);
    }

    const sessionBrowserNotification = selectNotification(
        getState(),
        session?.data?.['callId'],
    );
    sessionBrowserNotification?.close();

    if (
        rtcSessionRepresentsCentionErrand(session) &&
        SHOULD_DELAY_REMOVAL_OF_SESSION_OF_TYPE_CENTION_ERRAND(getState()) &&
        data &&
        data.cause === 'Terminated'
    ) {
        // This here to avoid having calls/errands 'ring' out to the user directly after an ongoing session for an
        // omnichannel errand has been terminated.
        // The reason for this is that we want to be as sure as we can be that the wrapup profile, if one exists, has
        // been activated.
        setTimeout(() => {
            dispatch({
                type: Actions.PHONE_REMOVE_SESSION,
                callId: session.data['callId'],
            });
        }, 1000);
    } else {
        dispatch({
            type: Actions.PHONE_REMOVE_SESSION,
            callId: session.data['callId'],
        });
    }

    dispatch(putMeetingOnHold(false));
};

const BAD_LOCATION_INFORMATION = 424;

const OUTSIDE_THE_FENCE_REASON = 'outside the fence';

const isOutsideTheFence = (reasonPhrase) =>
    `${reasonPhrase ?? ''}`.toLowerCase().includes(OUTSIDE_THE_FENCE_REASON);

export const handleSIPFailed = (data, session) => (dispatch) => {
    sendCallEndedMetric(data, session);
    addPhoneLogEntry(
        `Session failed with cause: "${data.cause}" -> attempting to stop locally generated tones`,
    );
    stopTones();

    if (JsSIP.C.causes.BUSY === data.cause) {
        addPhoneLogEntry(
            'Session failed. Reason: busy. Attempting to play locally generated busy tone',
        );
        playBusyTone();
    }

    if (JsSIP.C.causes.USER_DENIED_MEDIA_ACCESS === data.cause) {
        addPhoneLogEntry(
            'Session failed. Reason: User denied media access - mic permission turned off?',
        );
        dispatch(setMicPermission(false));
    }

    if (JsSIP.C.causes.UNAVAILABLE === data.cause) {
        addPhoneLogEntry(
            'Session failed. Reason: Callee is unavailable. Attempting to play locally generated disconnect tone',
        );
        playDisconnectTone();
    }

    if (JsSIP.C.causes.NOT_FOUND === data.cause) {
        addPhoneLogEntry(
            'Session failed. Reason: Callee returned not found. Attempting to play locally generated disconnect tone',
        );
        playDisconnectTone();
    }

    if (data.message?.status_code === BAD_LOCATION_INFORMATION) {
        addPhoneLogEntry(
            `Session failed. Reason: the proxy refused the location: ${data.message.reason_phrase}`,
        );
        const outsideTheFence = isOutsideTheFence(data.message.reason_phrase);

        dispatch(
            setSnackbar({
                i18nextMessage: outsideTheFence
                    ? 'calling.outside_permitted_region'
                    : 'calling.location_not_confirmed',
                hasCloseButton: true,
            }),
        );

        if (outsideTheFence) {
            dispatch(setGeoOutsideFence(true));
            dispatch(reportOutsideRegionToSurfaces());
        }
    }

    dispatch(onSessionEndedOrFailed(data, session));
};

const addConnectionEventListeners = (session, dispatch) => {
    const connection = session.connection;

    addUserPhoneLogEntry('ICE signaling state: ', connection.signalingState);
    connection.onsignalingstatechange = () => {
        addUserPhoneLogEntry(
            'ICE signaling state: ',
            connection.signalingState,
        );
    };

    addUserPhoneLogEntry(
        'RTCPeerConnection state: ',
        connection.connectionState,
    );
    connection.onconnectionstatechange = () => {
        addUserPhoneLogEntry(
            'RTCPeerConnection state: ',
            connection.connectionState,
        );
    };

    addUserPhoneLogEntry(
        'ICE connection state: ',
        connection.iceConnectionState,
    );
    connection.oniceconnectionstatechange = () => {
        addUserPhoneLogEntry(
            'ICE connection state: ',
            connection.iceConnectionState,
        );

        if (connection.iceConnectionState === 'checking') {
            try {
                const iceTransport =
                    session.connection.getSenders()[0].transport.iceTransport;
                iceTransport.onselectedcandidatepairchange = () => {
                    const pair = iceTransport.getSelectedCandidatePair();
                    addUserPhoneLogEntry(
                        `ICE candidate pair selected, local: ${pair.local.address}:${pair.local.port} remote: ${pair.remote.address}:${pair.remote.port}`,
                    );
                };
            } catch (_e) {
                addUserPhoneLogEntry(
                    "Failed to get ice transport, that's weird!",
                );
            }
        }
    };

    addUserPhoneLogEntry('ICE gathering state: ', connection.iceGatheringState);
    connection.onicegatheringstatechange = () => {
        addUserPhoneLogEntry(
            'ICE gathering state: ',
            connection.iceGatheringState,
        );
    };

    if (connection.addTrack) {
        connection.ontrack = (event) =>
            dispatch(handleReceiveStream(event.streams[0], session));
    } else {
        connection.onaddstream = (event) =>
            dispatch(handleReceiveStream(event.stream, session));
    }
};

export const stopUserAgent = () => (_dispatch, getState) => {
    const { phone } = getState();
    const userAgent = phone.get('userAgent');

    stopWatchingGeoLocationPermission();

    if (userAgent) {
        isStarted = false;
        userAgent.stop();
    }
};

export const createInitialUserAgent = () => async (dispatch, getState) => {
    await updateSipProxies();

    // Awaited, and only where the restriction is deployed at all: it decides whether the softphone
    // may register without a position, so learning it after the fact would mean either a client
    // that stayed blocked with the switch off, or one that registered before it could be told to
    // stop.
    if (geoRestrictionIsDeployed()) {
        await dispatch(fetchGeoRestrictionEnabled());
    }

    if (selectGeoLocationIsReported(getState())) {
        await dispatch(resolveGeoLocationForSoftphone());
        dispatch(watchGeoLocationPermission());
    }

    dispatch(createUserAgent());
};

const registerSoftphoneWithGeoLocation = () => (_dispatch, getState) => {
    const state = getState();
    const userAgent = state.phone.get('userAgent');
    const loggedInWithTempPass = !!state.app.get('temporaryPasswordState');

    if (
        !userAgent ||
        !selectGeoLocationCoordinates(state) ||
        selectPositionIsUnavailable(state) ||
        selectSoftphoneIsRegistered(state) ||
        !getSoftphoneShouldBeRegistered(loggedInWithTempPass)
    ) {
        return;
    }

    addPhoneLogEntry(
        'Geo location: position available, registering the softphone',
    );
    setGeoLocationRegisterHeader(userAgent, state);
    userAgent.register();
};

const resolveGeoLocationForSoftphone = (timeoutMs) => (dispatch) =>
    dispatch(
        resolveGeoLocation(timeoutMs, () =>
            dispatch(registerSoftphoneWithGeoLocation()),
        ),
    );

export const retryGeoLocation = () => async (dispatch, getState) => {
    if (!selectGeoLocationIsReported(getState())) {
        return;
    }

    addPhoneLogEntry('Geo location: retrying for a position');
    await dispatch(resolveGeoLocationForSoftphone());
    dispatch(registerSoftphoneWithGeoLocation());
};

let stopGeoLocationPermissionWatch = null;

const stopWatchingGeoLocationPermission = () => {
    stopGeoLocationPermissionWatch?.();
    stopGeoLocationPermissionWatch = null;
};

const watchGeoLocationPermission = () => (dispatch) => {
    if (stopGeoLocationPermissionWatch) {
        return;
    }

    stopGeoLocationPermissionWatch = onGeoLocationPermissionGranted(
        async () => {
            addPhoneLogEntry('Geo location permission granted');

            await dispatch(resolveGeoLocationForSoftphone());
            dispatch(registerSoftphoneWithGeoLocation());
        },
    );
};

const setGeoLocationRegisterHeader = (userAgent, state) => {
    if (!selectGeoLocationIsReported(state)) {
        return;
    }

    const coordinates = selectGeoLocationCoordinates(state);

    userAgent
        .registrator()
        .setExtraHeaders(
            coordinates ? [geoLocationExtraHeader(coordinates)] : [],
        );
};

export const createUserAgent =
    (prevAttempts = 0) =>
    async (dispatch, getState) => {
        // Stop any existing UserAgent before creating a new one, so a second
        // boot can't leave two registrations live for the same user. Skip while
        // in a call: UA.stop() also terminates active RTCSessions (would drop
        // the call), matching how registrationExpiring/registrationFailed defer
        // teardown. stop() on an already-stopped UA (reconnect path) is a no-op.
        const existingUserAgent = getState().phone.get('userAgent');
        if (existingUserAgent && getNumLocalCalls(getState()) === 0) {
            addPhoneLogEntry(
                'createUserAgent called while a UserAgent already exists. Stopping the existing one to avoid duplicate SIP registrations.',
            );
            isStarted = false;
            existingUserAgent.stop();
        }
        // Clear the old UA's SIP OPTIONS keep-alive before the new UA overwrites
        // the (module-global) interval id below.
        clearInterval(sipOptionsIntervalId);

        let nextAttempt = prevAttempts + 1;

        const proxySockets = SIP_PROXIES.map((proxy) => {
            return {
                socket: new JsSIP.WebSocketInterface(proxy.socketUrl),
                weight: proxy.weight,
            };
        });

        const sipInfo = getOwnSipUserInfo(getState());
        const username = decryptSipInfo(sipInfo.get('username'));
        const password = decryptSipInfo(sipInfo.get('password'));

        const embedded = getState().app.get('embedded');
        const userAgentName = getUserAgentName(embedded);
        const loggedInWithTempPass = !!getState().app.get(
            'temporaryPasswordState',
        );

        const needsCompanion = selectCallingNeedsCompanion(getState());
        const geoBlocked =
            selectPositionIsUnavailable(getState()) || needsCompanion;

        if (geoBlocked) {
            addPhoneLogEntry(
                needsCompanion
                    ? 'A companion is required in this region and none is usable -- not registering the softphone'
                    : 'Location sharing is off and a position is required in this region -- not registering the softphone',
            );
        }

        const configuration = {
            sockets: [...proxySockets],
            uri: `sip:${username}@${sipInfo.get('server') || SIP_CLUSTER}`,
            password: password,
            session_timers: false,
            no_answer_timeout: 150,
            register:
                getSoftphoneShouldBeRegistered(loggedInWithTempPass) &&
                !geoBlocked,
            user_agent: userAgentName,
            connection_recovery_max_interval: MAX_BACKOFF_MS / 1000,
        };

        const userAgent = new JsSIP.UA(configuration);

        setGeoLocationRegisterHeader(userAgent, getState());

        userAgent.on('newRTCSession', (callData) => {
            const state = getState();
            const { phone } = state;

            const session = callData.session;
            const callId = callData.request.getHeader('Call-ID');
            const simlogFromDate = getCurrentDateMinusOneDayForSimlog();
            addPhoneLogEntry(
                `New RTC session\n\nhttp://simlog1.service.telavox.se:8099/?from=${simlogFromDate}&callid=${callId}`,
            );

            const isVideoMeetingCall = callData.request.getHeader(
                'X-TVX-IS-VIDEO-MEETING-CALL',
            );
            if (isVideoMeetingCall) {
                session.data.callId = callId;
                session.data.isMeeting = true;
                dispatch(setActiveMeetingCallIdAction(callId));
                // It's a meeting, it has it's own event handlers
                return;
            }

            if (rtcSessionRepresentsCentionErrand(session)) {
                const deepLinkId = getCentionDataFromRtcSession(
                    session,
                    OMNICHANNEL_PROPERTIES.DEEP_LINK.value,
                );
                addOmniChannelLogEntry(
                    'Incoming omnichannel errand with deepLinkId: ',
                    deepLinkId,
                );

                if (
                    !TREAT_CENTION_ERRANDS_LIKE_PHONE_CALLS_EXPERIMENTAL_FEATURE(
                        getState(),
                    )
                ) {
                    // The standard behaviour, as it was implemented, is that we display a notification for
                    // omni errands (executed below, in onNewOmnichannelSession), and do not set any
                    // session to the state (see return statement below).
                    // This has the implication that there are cases where we display a notification for
                    // an incoming errand (an email, or some other supported channel that is not a phone call)
                    // at the same time as we display the call canvas for a phone call.
                    // Maybe this isn't how it should work(?) but as this is how it was implemented, we start out with
                    // testing handling omni errands just like phone calls for one specific customer only
                    // (...i.e. for that customer continuing with the code outside this if block.
                    // Most importantly: setting the session to the state, which causes us to terminate other incoming
                    // sessions while the omni errand is 'ringing' out to the user)
                    dispatch(onNewOmnichannelSession(session));
                    return;
                }
            }

            if (!callData.request.getHeader('X-TVX-LID')) {
                const xTvxLid = nanoid();
                callData.request.setHeader('X-TVX-LID', xTvxLid);
            }

            session.data['callId'] = callId;
            const lid = callData.request.getHeader('X-TVX-LID');
            session.data['logId'] = lid;
            const agentInfo = callData.request.getHeader('X-TVX-AGENT-INFO');
            const isUnParking = phone.get('unparking');
            const takeMeetingIntoAccount =
                selectMeetingIsActive(state) && getVideoConferenceWithSip();
            const numberOfCalls = takeMeetingIntoAccount
                ? getNumLocalCalls(getState()) + 1
                : getNumLocalCalls(getState());
            const isMultipleCall = numberOfCalls > 0;
            const isQueueCall = callData.request.getHeader('X-Tvx-Queuename');
            session.data['isQueueCall'] = !!isQueueCall;

            if (isMultipleCall && session.direction === 'incoming') {
                const newCallBlockReason = hasActiveCallsBlockingNewCall(
                    state,
                    'incoming',
                    2,
                );
                if (
                    newCallBlockReason === NewCallBlockReason.NONE ||
                    blocksOnlyOrigination(newCallBlockReason)
                ) {
                    dispatch(
                        segmentTrack(EVENT_STRINGS.USER_GOT_ANOTHER_CALL, {
                            callId: callId,
                            callWaitingEnabled: getCallWaitingEnabled(state),
                            reasonToBlockCall: newCallBlockReason,
                        }),
                    );
                } else {
                    addUserPhoneLogEntry(
                        'New RTC session -> terminating session with callId: ' +
                            callId +
                            ' reason: ',
                        newCallBlockReason.key,
                    );
                    session.terminate(BUSY_TERMINATE_OPTIONS);
                    return;
                }
            }

            session.data.webrtcStats = [];
            session.data.lastDumpedStatEntry = 0;

            if (isMultipleCall) {
                dispatch(hsMultipleCalls());
            }

            const isTelavoxTicketNonCall =
                rtcSessionRepresentsTicketNonVoice(session);
            const telavoxTicketData = isTelavoxTicketNonCall
                ? getTicketDataFromRtcSessionRequest(session)
                : undefined;
            const telavoxTicketId = telavoxTicketData
                ? telavoxTicketData.ticketId
                : undefined;

            if (isTelavoxTicketNonCall) {
                addTicketLogEntry(
                    `Incoming SIP session for ticket: ${telavoxTicketId}`,
                );
                dispatch(
                    spawnNotificationNewTicket(
                        `ticket-${telavoxTicketId}`,
                        telavoxTicketData?.message,
                        telavoxTicketData?.channel,
                        callId,
                    ),
                );
            }

            dispatch({
                type: Actions.PHONE_NEW_SESSION,
                sessionObject: session,
                callId: callId,
                ...(agentInfo && { agentInfo: JSON.parse(atob(agentInfo)) }),
                isTelavoxTicketNonCall: isTelavoxTicketNonCall,
                telavoxTicketData: telavoxTicketData,
                telavoxTicketId: telavoxTicketId,
            });

            if (session.direction === 'incoming') {
                dispatch(hsIncomingCall(session.data['callId']));
            } else {
                dispatch(hsOutgoingCall(session.data['callId']));
            }

            if (isUnParking) {
                addPhoneLogEntry('Unparking, attempting to "answer" call');
                dispatch(answerCall(session));
                //Needed for the gui not to jump, and show the answer call button
                setTimeout(
                    () => dispatch({ type: Actions.FINISH_UNPARKING }),
                    400,
                );
            } else if (callShouldBeAnsweredAutomatically(callData)) {
                addPhoneLogEntry('Answering call automatically');
                dispatch(answerCall(session));
            }

            session.on('reinvite', (data) => {
                const paiHeader = data.request.getHeader('P-Asserted-Identity');

                if (paiHeader) {
                    const match = filterOutPaiHeader(paiHeader);
                    if (match) {
                        session.data.pai = match;
                        dispatch(setPaiHeader(session.data.pai));
                        addPhoneLogEntry(
                            `Found PAI-header in reinvite: ${match}`,
                        );
                    }
                }
            });

            session.on('accepted', () => {
                if (isMultipleCall && session.direction === 'incoming') {
                    dispatch(
                        segmentTrack(EVENT_STRINGS.USER_ACCEPTED_SECOND_CALL, {
                            callId: callId,
                        }),
                    );
                }
                addPhoneLogEntry(
                    'Session accepted -> Attempting to stop locally generated tones',
                );
                clearInterval(
                    session.data.rtpPollingIntervalForDeterminingProgressTones,
                );
                stopTones();
                dispatch({
                    type: Actions.PHONE_SESSION_ACCEPTED,
                    callId: session.data['callId'],
                });
            });

            session.on('progress', (data) => {
                addPhoneLogEntry('Session progress');
                if (rtcSessionRepresentsTicketNonVoice(session)) {
                    addPhoneLogEntry(
                        'Session is ticket non voice, no-op on progress',
                    );
                    return;
                }

                // Don't play any ringtones in incoming calls that we auto answer, that
                // could lead to ringtones being played after the call has been answered
                if (
                    session.direction === 'incoming' &&
                    !isUnParking &&
                    !callShouldBeAnsweredAutomatically(callData)
                ) {
                    addPhoneLogEntry(
                        'Session progress -> call is incoming. Attempting to play locally generated ringtone',
                    );
                    if (
                        getCallWaitingEnabled(getState()) &&
                        getLocalCallIds(getState()).length > 1
                    ) {
                        ToneGenerator.playCallWaitingTone();
                    } else {
                        dispatch(playRingTone());
                    }
                } else if (session.direction === 'outgoing') {
                    addPhoneLogEntry('Session progress -> call is outgoing');

                    //https://tools.ietf.org/html/rfc3960
                    if (data.response.status_code === 180) {
                        /**
                         * !inboundAudioRtpReceived is to avoid having progress tones played from more than one source.
                         */
                        const inboundAudioRtpReceived =
                            session.data['inboundAudioRtpReceived'];
                        if (!inboundAudioRtpReceived) {
                            addPhoneLogEntry(
                                'Session progress -> call is outgoing -> Attempting to play locally generated progress tone',
                            );
                            dispatch(playRingbackTone());
                        }
                    }
                }
            });

            session.on('confirmed', () => {
                dispatch(applyNoiseSuppression(session));
                dispatch(
                    hsConfirmCall(
                        session.data['callId'],
                        session.direction === 'incoming',
                        session.data.answeredThroughHeadset,
                    ),
                );

                const getInitialStats = () => {
                    return {
                        outgoingAudioLevel: 0,
                        currentRoundTripTime: 0,
                        packetsReceived: 0,
                        packetsLost: 0,
                        jitter: 0,
                        timestamp: 0,
                        incomingAudioLevel: 0,
                        jitterBufferDelay: 0,
                        jitterBufferEmittedCount: 0,
                        packetsSent: 0,
                        retransmittedPacketsSent: 0,
                        remotePacketsLost: 0,
                        remoteJitter: 0,
                        remoteRoundTripTime: 0,
                    };
                };
                session.data.webrtcStats.push(getInitialStats());
                let statIteration = 0;

                session.data.webrtcStatsTimer = setInterval(() => {
                    session.connection.getStats().then((stats) => {
                        const statsToSave = getInitialStats();
                        const candidatePairs = {};
                        const candidates = {};
                        stats.forEach((report) => {
                            if (report.type === 'media-source') {
                                statsToSave['outgoingAudioLevel'] =
                                    report.audioLevel;
                            } else if (report.type === 'inbound-rtp') {
                                statsToSave['packetsReceived'] =
                                    report.packetsReceived;
                                statsToSave['packetsLost'] = report.packetsLost;
                                statsToSave['jitter'] = report.jitter;
                                statsToSave['timestamp'] = report.timestamp;
                            } else if (
                                report.type === 'track' &&
                                report.remoteSource
                            ) {
                                statsToSave['incomingAudioLevel'] =
                                    report.audioLevel;
                                statsToSave['jitterBufferDelay'] =
                                    report.jitterBufferDelay;
                                statsToSave['jitterBufferEmittedCount'] =
                                    report.jitterBufferEmittedCount;
                            } else if (report.type === 'outbound-rtp') {
                                statsToSave['packetsSent'] = report.packetsSent;
                                statsToSave['retransmittedPacketsSent'] =
                                    report.retransmittedPacketsSent;
                            } else if (report.type === 'remote-inbound-rtp') {
                                statsToSave['remotePacketsLost'] =
                                    report.packetsLost;
                                statsToSave['remoteJitter'] = report.jitter;
                                statsToSave['remoteRoundTripTime'] =
                                    report.roundTripTime;
                            } else if (report.type === 'candidate-pair') {
                                candidatePairs[report.id] = report;
                            } else if (
                                report.type === 'remote-candidate' ||
                                report.type === 'local-candidate'
                            ) {
                                candidates[report.id] = report;
                            }
                        });
                        if (Object.keys(candidatePairs).length !== 0) {
                            statsToSave.candidatePairs = Object.values(
                                candidatePairs,
                            ).reduce(
                                (
                                    prev,
                                    {
                                        id,
                                        requestsSent,
                                        consentRequestsSent,
                                        responsesReceived,
                                        currentRoundTripTime,
                                        state,
                                        remoteCandidateId,
                                        localCandidateId,
                                    },
                                ) => {
                                    prev[id] = {
                                        requestsSent,
                                        consentRequestsSent,
                                        responsesReceived,
                                        currentRoundTripTime,
                                        state,
                                        remoteIp:
                                            candidates[remoteCandidateId].ip,
                                        remotePort:
                                            candidates[remoteCandidateId].port,
                                        localIp:
                                            candidates[localCandidateId].ip,
                                        localPort:
                                            candidates[localCandidateId].port,
                                    };
                                    return prev;
                                },
                                {},
                            );
                        }

                        session.data.webrtcStats.push(statsToSave);
                        statIteration++;

                        if (statIteration % 10 === 0) {
                            try {
                                const statsToReport = calculateStatsOverTime(
                                    session.data.webrtcStats,
                                    session.data.lastDumpedStatEntry,
                                );
                                session.data.lastDumpedStatEntry =
                                    session.data.webrtcStats.length - 1;
                                addUserPhoneLogEntry(
                                    'Call stats: ',
                                    statsToReport,
                                );
                            } catch (error) {
                                addUserPhoneLogEntry(
                                    `Call stats: failed to calculate stats with error: ${error}`,
                                );
                            }
                        }
                    });
                }, 1000);
                if (getPhoneIsMuted(getState())) {
                    session.mute();
                }
            });

            session.on('ended', (data) => {
                try {
                    const statsToReport = calculateStatsOverTime(
                        session.data.webrtcStats,
                        0,
                    );
                    addUserPhoneLogEntry('Final call stats: ', statsToReport);
                } catch (error) {
                    addUserPhoneLogEntry(
                        `Final call stats: failed to calculate stats with error: ${error}`,
                    );
                }

                sendCallEndedMetric(data, session);
                addPhoneLogEntry(
                    'Session ended -> attempting to stop locally generated tones',
                );
                stopTones();
                if (
                    data.originator !== 'local' &&
                    !rtcSessionRepresentsTicketNonVoice(session)
                ) {
                    addPhoneLogEntry(
                        'Session ended -> attempting to play locally generated disconnect tone',
                    );
                    playDisconnectTone();
                }

                dispatch(onSessionEndedOrFailed(data, session));
            });
            session.on('failed', (data) => {
                dispatch(handleSIPFailed(data, session));
            });
            session.on('muted', () => {
                addPhoneLogEntry(
                    `Muted session with callid ${session.data.callId}`,
                );
                dispatch(hsMute(session.data.callId));
            });
            session.on('unmuted', () => {
                addPhoneLogEntry(
                    `Unmuted session with callid ${session.data.callId}`,
                );
                dispatch(hsUnmute(session.data.callId));
            });
            session.on('sdp', (data) => {
                if (data.originator === 'local') {
                    data.sdp = filterOutTCPCandidates(data.sdp);
                } else if (
                    data.originator === 'remote' &&
                    data.type === 'offer'
                ) {
                    data.sdp = filterOutCryptoLineInSDP(data.sdp);
                }
                if (session.data.iceTimeoutTimer) {
                    clearTimeout(session.data.iceTimeoutTimer);
                }
            });
            session.on('icecandidate', (data) => {
                if (!session.data.iceTimeoutTimer) {
                    session.data.iceTimeoutTimer = setTimeout(() => {
                        data.ready();
                    }, 500);
                }
            });
            session.on('hold', (data) => {
                if (data.originator !== 'local') {
                    return;
                }

                dispatch({
                    type: Actions.PHONE_ISONHOLD,
                    isOnHold: true,
                    callId: session.data.callId,
                });

                dispatch(hsHoldCall(session.data['callId']));
            });
            session.on('unhold', (data) => {
                if (data.originator !== 'local') {
                    return;
                }

                dispatch({
                    type: Actions.PHONE_ISONHOLD,
                    isOnHold: false,
                    callId: session.data.callId,
                });

                dispatch(hsUnholdCall(session.data['callId']));
            });

            /*
                    This section is to ensure we don't miss any jsSIP event
                    The array consists of the found session events in the jsSIP RTCSession code.
                    We gather these events, and log that they have occurred for debugging purposes

                    This is needed as the #catchJssipDebugLogs method cant catch errors sent on
                    jsSIP
                */
            const unhandledEvents = [
                'getusermediafailed',
                'peerconnection:setremotedescriptionfailed',
                'newDTMF',
                'newInfo',
                'peerconnection',
                'peerconnection:createofferfailed',
                'peerconnection:createanswerfailed',
                'peerconnection:setlocaldescriptionfailed',
                'update',
                'refer',
                'replaces',
                'sending',
                'connecting',
                '_failed',
            ];
            unhandledEvents.forEach((event) => {
                session.on(event, (args) => {
                    let jsonArgs;

                    // Try to avoid "TypeError: Converting circular structure to JSON"
                    try {
                        jsonArgs = args && JSON.stringify(args);
                    } catch (_e) {
                        jsonArgs = undefined;
                    }

                    let unhandledMessage = `[jsSIP UNHANDLED EVENT] The following event occurred: ${event}`;
                    unhandledMessage = jsonArgs
                        ? unhandledMessage.concat(`\nArguments:\n${jsonArgs}`)
                        : unhandledMessage;
                    addPhoneLogEntry(unhandledMessage);
                });
            });
        });

        userAgent.on('connected', () => {
            nextAttempt = 1;
            addPhoneLogEntry(
                'userAgent connected -> setting phone connected to true',
            );
            addSipConnectionEstablishedDiv();
            dispatch(setPhoneConnected(true));
            addPhoneLogEntry('Sending SIP OPTIONS every 10000ms');
            sendEmptySipOptions(userAgent, getState());
        });
        userAgent.on('disconnected', async (data) => {
            const code = (data && data.code) || 'No code';
            const reason = (data && data.reason) || 'No reason';
            const host =
                (data && data.socket && data.socket.url) || 'No host url';
            addPhoneLogEntry(
                `userAgent disconnected from '${host}' with message '${code}: ${reason}'`,
            );
            if (isStarted) {
                // Don't want to reconnect if disconnected because user agent has been stopped on purpose

                // JsSIP takes care of load balancing, reconnection on errors, and
                // back-off. If we have a non-clean shutdown however, we'll take the
                // opportunity to make sure our proxy hasn't been removed from the load balancing.
                await updateSipProxiesThrottled();
                if (data && data.error && shouldReconnectSocket(userAgent)) {
                    addPhoneLogEntry(
                        'Our proxy is no longer in the list of SIP-proxies. Reconnecting socket.',
                    );
                    dispatch(restartUserAgent(userAgent, nextAttempt));
                }
            }
            clearInterval(sipOptionsIntervalId);
            addPhoneLogEntry('Stopping to send SIP OPTIONS');
            dispatch(setPhoneConnected(false));
        });
        userAgent.on('registered', () => {
            nextAttempt = 1;
            addPhoneLogEntry(
                'userAgent registered -> setting phone registered to true',
            );
            dispatch(setPhoneRegistered(true));
        });
        userAgent.on('unregistered', (data) => {
            const cause = (data && data.cause) || 'No cause';
            addPhoneLogEntry(`Event 'unregistered'. Cause: '${cause}'`);
            dispatch(setPhoneRegistered(false));
        });
        userAgent.on('registrationExpiring', async () => {
            addPhoneLogEntry("Event 'registrationExpiring'");

            if (selectGeoLocationIsReported(getState())) {
                await dispatch(resolveGeoLocationForSoftphone(GEO_FIX_WAIT_MS));
                setGeoLocationRegisterHeader(userAgent, getState());
            }

            const hasSessions = getNumLocalCalls(getState()) > 0;
            // Don't stop the UA if the user is in a call!
            if (!hasSessions) {
                addPhoneLogEntry('User not in call. Refetching SIP-proxies.');
                await updateSipProxies();
                if (shouldReconnectSocket(userAgent)) {
                    addPhoneLogEntry(
                        'Our proxy is no longer in the list of SIP-proxies. Reconnecting socket.',
                    );
                    dispatch(restartUserAgent(userAgent));
                } else {
                    addPhoneLogEntry(
                        'Sending SIP re-registration. Reusing same SIP-proxy.',
                    );
                    userAgent.register();
                }
            } else {
                addPhoneLogEntry(
                    'User in call. Sending SIP re-registration. Reusing same SIP-proxy.',
                );
                userAgent.register();
            }
        });
        userAgent.on('registrationFailed', (data) => {
            const response = (data && data.response) || 'No response';
            const cause = (data && data.cause) || 'No cause';
            addPhoneLogEntry(
                `Event 'registrationFailed'. Response: '${response}', cause: '${cause}'`,
            );
            dispatch(setPhoneRegistered(false));
            const hasSessions = getNumLocalCalls(getState()) > 0;
            if (hasSessions) {
                registerUserAgentOnCallEnded = true;
            } else {
                dispatch(restartUserAgent(userAgent, nextAttempt));
            }
        });

        userAgent.start();
        isStarted = true;

        dispatch({ type: Actions.PHONE_NEW_UA, userAgent: userAgent });
    };

const shouldReconnectSocket = (userAgent) => {
    const currentProxy = userAgent.transport.socket.url;
    const proxyList = SIP_PROXIES.map((e) => e.socketUrl);
    return !proxyList.includes(currentProxy);
};

const sendEmptySipOptions = (userAgent, state) => {
    if (!userAgent) return;

    const sipInfo = getOwnSipUserInfo(state);
    const username = decryptSipInfo(sipInfo?.get('username'));
    const server = sipInfo?.get('server');
    if (!username || !server) return;

    const target = `sip:${username}@${server}`;
    const eventHandlers = {
        failed: () => addPhoneLogEntry('Error sending SIP OPTIONS'),
    };

    const options = { eventHandlers };

    sipOptionsIntervalId = setInterval(() => {
        userAgent.sendOptions?.(target, null, options);
    }, 10000);
};

const restartUserAgent =
    (userAgent, nextAttempt = 1) =>
    (dispatch) => {
        // Stopping the UA disconnects all sockets
        addPhoneLogEntry('Stopping UserAgent');
        isStarted = false;
        userAgent.stop();
        // It takes at most 2 seconds for the UserAgent to be stopped.
        // Add this to the backoff to make sure we don't try to connect at the same time.
        const backoffTime = 2000 + calculateBackoffTime(nextAttempt - 1);
        addPhoneLogEntry(
            `Making reconnection attempt #${nextAttempt} in ${backoffTime} ms`,
        );
        const timeoutId = setTimeout(() => {
            dispatch(createUserAgent(nextAttempt));
        }, backoffTime);
        dispatch(setSipConnectionRetryTimeoutId(timeoutId));
    };

const setSipConnectionRetryTimeoutId = (timeoutId) => ({
    type: Actions.SIP_RETRY_TIMEOUT_ID,
    timeoutId,
});

const setPhoneRegistered = (phoneRegistered) => (dispatch) => {
    dispatch({ type: Actions.PHONE_REGISTERED, registered: phoneRegistered });
};

const setPhoneConnected = (phoneConnected) => (dispatch) => {
    dispatch({ type: Actions.PHONE_CONNECTED, connected: phoneConnected });
};

export const setPhoneEnabled = (phoneEnabled) => (dispatch) => {
    dispatch({ type: Actions.PHONE_ENABLED, enabled: phoneEnabled });
};

export const call =
    (number, callMethod, extraCallOptions) => async (dispatch, getState) => {
        const { phone } = getState();

        const phoneEnabled = phone.get('enabled');
        const newCallBlockReason = hasActiveCallsBlockingNewCall(
            getState(),
            'outgoing',
            2,
        );

        addPhoneLogEntry(
            `About to perform call to number ${number} with call method ${callMethod}`,
        );

        if (newCallBlockReason !== NewCallBlockReason.NONE) {
            addUserPhoneLogEntry(
                `NOT performing new call, blocked by: ${newCallBlockReason}`,
            );
            return;
        }

        const sessions = getLocalCallSessions(getState());
        const holdSuccess = await holdAllSessions(sessions);
        if (!holdSuccess) {
            addUserPhoneLogEntry(
                'Failed to start outgoing call, could not hold current sessions',
            );
        }

        // Re installed due to WEB-1556 so that customers with e.g. cisco terminals will be able to use
        // the web app as an redirect platform.

        const autoAnswerWithIpPhone = getAutoAnswerWithIpPhone(getState());
        const strippedNumber = removeExtraCharacters(number);
        addUserPhoneLogEntry(
            `Formatted number ${number} to ${strippedNumber} prior to performing call`,
        );

        // Model A: in companion mode this surface owns no media, so it must never dial locally -
        // ask the companion to dial from its own SIP UA instead, and the call lands on the machine
        // the user is actually at. This deliberately sits *above* the phoneEnabled check: that flag
        // means "softphone capability is available on this client" (see isPhoneEnabled in
        // app-actions), not "registered", so it is true for a normal Chrome CTI and would otherwise
        // send us straight to the local-dial switch below.
        //
        // Gated on the *surface*, not just the feature switch: the switch is per user, so it is also
        // true in that user's ordinary web app, which owns its own softphone and must keep dialling
        // locally. The surface check also carries the user's "Use Companion for calls" choice, so
        // turning that off drops straight through to the local paths below.
        //
        // Unlike mute and DTMF this cannot use shouldRelayToCompanion: at dial time there is no
        // session yet, so there is nothing to check for locally-owned media.
        //
        // canRelayCallToCompanion rather than the surface check alone, because falling through is a
        // working call and relaying blindly is a lost one: with no designated device, or one nobody
        // has opened in a fortnight, the push goes nowhere and nothing tells the user. The paths
        // below still ring something.
        if (canRelayCallToCompanion(getState())) {
            const designated = selectDesignatedCompanionDevice(getState());
            addUserPhoneLogEntry(
                `Call -> companion mode is on, delegating call to the companion ${designated.get('deviceId')}`,
            );
            callOnCompanion(strippedNumber);
            return;
        }

        if (isCompanionSignallingSurface(getState())) {
            addUserPhoneLogEntry(
                'Call -> companion mode is on but no companion is reachable, falling back to this client',
            );
        }

        if (!phoneEnabled) {
            addUserPhoneLogEntry(
                `Call -> softphone not available, defaulting to starting call through Home. AutoAnswer: ${autoAnswerWithIpPhone}`,
            );
            dispatch(
                startSipCallThroughHome(strippedNumber, autoAnswerWithIpPhone),
            );
            return;
        }
        switch (callMethod) {
            case CallMethodType.LOCAL:
                dispatch(startLocalCall(strippedNumber, extraCallOptions));
                break;
            case CallMethodType.IP_PHONE:
                dispatch(
                    startSipCallThroughHome(
                        strippedNumber,
                        autoAnswerWithIpPhone,
                    ),
                );
                break;
            case CallMethodType.MOBILE:
                dispatch(startMobileCallThroughHome(strippedNumber));
                break;
            default:
                console.warn('Unrecognized call method');
        }
    };

export const holdCall = (session) => (_dispatch, getState) => {
    if (!session) {
        session = getFocusedSession(getState());
    }

    if (!session) {
        addPhoneLogEntry('Cannot hold call, no session found');
        return;
    }

    addPhoneLogEntry('Client using HOLD to park the current call session.');
    session.hold();
};

export const unholdCall = (session) => (_dispatch, getState) => {
    if (!session) {
        session = getFocusedSession(getState());
    }

    if (!session) {
        addPhoneLogEntry('Cannot unhold call, no session found');
        return;
    }

    addPhoneLogEntry('Client unparks the current call session via UNHOLD.');
    session.unhold();
};

export const startLocalCall =
    (number, extraCallOptions = {}) =>
    async (dispatch, getState) => {
        if (selectGeoLocationIsReported(getState())) {
            await dispatch(ensureFreshGeoLocation());
        }

        if (selectPositionIsUnavailable(getState())) {
            addPhoneLogEntry(
                'NOTE: Location sharing is off -- not going through with making the call',
            );
            dispatch(
                setSnackbar({
                    i18nextMessage: 'permission.location.not_granted',
                    hasCloseButton: true,
                }),
            );
            return;
        }

        const microphoneAllowed = await permissionToMicrophoneIsGranted();

        if (microphoneAllowed) {
            dispatch(logAudioDeviceDataIfAppropriate());
            dispatch(setCallOptionsAndPerformCall(number, extraCallOptions));
        } else {
            addPhoneLogEntry(
                'NOTE: Mic permission was not granted -- not going through with making the call',
            );
            dispatch(setMicPermission(false));
        }
    };

const setCallOptionsAndPerformCall =
    (number, extraCallOptions = {}) =>
    (dispatch, getState) => {
        const { phone } = getState();

        const mediaConstraints = getMediaConstraints(getState(), true);

        const OPTIONS = Object.assign({}, extraCallOptions, {
            mediaConstraints: mediaConstraints,
        });

        const geoCoordinates = selectGeoLocationIsReported(getState())
            ? selectGeoLocationCoordinates(getState())
            : null;

        if (geoCoordinates) {
            OPTIONS.extraHeaders = [
                ...(extraCallOptions.extraHeaders || []),
                geoLocationExtraHeader(geoCoordinates),
            ];
        }

        // Check userAgent
        const userAgent = phone.get('userAgent');
        if (!userAgent) {
            addPhoneLogEntry(
                'ERROR: No phone user agent, CAN NOT perform new call',
            );
            return;
        }

        const sipInfo = getOwnSipUserInfo(getState());
        const sipCluster = sipInfo?.get('server') || SIP_CLUSTER;
        const target = `sip:${number}@${sipCluster}`;
        const session = userAgent && userAgent.call(target, OPTIONS);
        const peerConnection = session && session.connection;

        session.data.rtpPollingIntervalForDeterminingProgressTones =
            setInterval(() => {
                peerConnection
                    .getStats()
                    .then((stats) => {
                        stats.forEach((stat) => {
                            if (
                                (stat.kind === 'audio' ||
                                    stat.mediaType === 'audio') &&
                                stat.type === 'inbound-rtp' &&
                                stat.packetsReceived
                            ) {
                                stopTones('inbound audio rtp received');
                                session.data['inboundAudioRtpReceived'] = true;
                                clearInterval(
                                    session.data
                                        .rtpPollingIntervalForDeterminingProgressTones,
                                );
                            }
                        });
                    })
                    .catch((error) => {
                        if (errorMessageIsRTCPeerConnectionGone(error)) {
                            stopTones('inbound audio rtp received');
                            session.data['inboundAudioRtpReceived'] = true;
                            clearInterval(
                                session.data
                                    .rtpPollingIntervalForDeterminingProgressTones,
                            );
                        }

                        getFaro()?.api.pushError(error);
                        addGeneralLogEntry(`Error reported: ${error}`);
                    });
            }, 200);

        addConnectionEventListeners(session, dispatch);
    };

const startSipCallThroughHome =
    (number, autoAnswer = false) =>
    (dispatch) => {
        dispatch(startCallThroughHome(number, 'SIP', autoAnswer));
    };

const startMobileCallThroughHome = (number) => (dispatch) => {
    dispatch(startCallThroughHome(number, 'MEX'));
};

const startCallThroughHome = (number, callType, autoAnswer = false) => {
    const activeCallDTO = { type: callType, number: number };

    return (_dispatch, getState) => {
        const extensionKey = getOwnExtensionKey(getState());

        ExtensionResource.createActiveCall(
            {
                onResult() {
                    console.info('Call successfully started');
                },
            },
            extensionKey,
            autoAnswer,
            activeCallDTO,
        );
    };
};

export const answerOrHangupCall = (dispatch, getState) => {
    const state = getState();
    const session = getFocusedSession(state);

    if (session) {
        if (session.direction === 'incoming' && !session.isEstablished()) {
            /**
             * Incoming call, not yet answered
             */
            dispatch(answerCall(session));
        } else if (session.isEstablished()) {
            /**
             * Ongoing call
             */
            dispatch(hangupSession(session));
        }
    } else {
        addPhoneLogEntry(
            'Cannot answer or hang up call, no session to answer or hangup found',
        );
    }
};

const holdAllSessions = (sessions, exception = undefined) => {
    const holdSuccess = sessions.every((iteratedSession) => {
        if (exception && iteratedSession === exception) {
            return true;
        }
        if (iteratedSession.isOnHold().local) {
            return true;
        }
        if (!iteratedSession.isEstablished()) {
            return false;
        }
        return iteratedSession.hold();
    });
    return Promise.resolve(holdSuccess);
};

export const unholdAllSessions = (sessions, exception = undefined) => {
    const unholdSuccess = sessions.every((iteratedSession) => {
        if (exception && iteratedSession === exception) {
            return true;
        }
        if (!iteratedSession.isOnHold().local) {
            return true;
        }
        if (!iteratedSession.isEstablished()) {
            return false;
        }
        return iteratedSession.unhold();
    });
    return Promise.resolve(unholdSuccess);
};

export const answerCall =
    (session, throughHeadset = false, answerHere = false) =>
    async (dispatch, getState) => {
        const state = getState();

        if (!session) {
            session = getFocusedSession(state);
        }

        if (!session) {
            addPhoneLogEntry('Cannot answer call, no session found');
            return;
        }

        if (
            !answerHere &&
            selectCompanionAnswersCalls(state) &&
            isRingingIncomingCall(session)
        ) {
            const callId = session.data['callId'];
            addUserPhoneLogEntry(
                `Answer -> handing call ${callId} to the desktop app`,
            );
            answerCompanionCall(callId);
            setTimeout(() => {
                if (isRingingIncomingCall(session)) {
                    addUserPhoneLogEntry(
                        `Answer -> the desktop app did not take call ${callId}, answering here`,
                    );
                    dispatch(answerCall(session, throughHeadset, true));
                }
            }, COMPANION_ANSWER_FALLBACK_MS);
            return;
        }
        dispatch(putMeetingOnHold(true));

        const sessions = getLocalCallSessions(state);
        if (getCallWaitingEnabled(state) && sessions.size > 1) {
            const holdSuccess = await holdAllSessions(sessions, session);
            if (holdSuccess) {
                addUserPhoneLogEntry(
                    'Put other calls on hold when answering call with callId: ',
                    session.data['callId'],
                );
            } else {
                addUserPhoneLogEntry(
                    'Could not answer call with callId: ',
                    session.data['callId'],
                    ' because other calls could not be put on hold',
                );
                return;
            }
        }

        const mediaConstraints = getMediaConstraints(getState(), true);

        const OPTIONS = Object.assign(
            {},
            { mediaConstraints: mediaConstraints },
        );

        if (selectGeoLocationIsReported(getState())) {
            await dispatch(ensureFreshGeoLocation());
            const coordinates = selectGeoLocationCoordinates(getState());

            if (coordinates) {
                OPTIONS.extraHeaders = [geoLocationExtraHeader(coordinates)];
            }
        }

        if (rtcSessionRepresentsTicketNonVoice(session)) {
            const ticketData = getTicketDataFromRtcSessionRequest(session);
            const ticketId = ticketData['ticketId'];
            if (ticketId) {
                addTicketLogEntry(
                    `SIP session answered. Routing user to ticket ${ticketId}`,
                );
                history.push(
                    generatePath(routes['tickets.ticketKey'], {
                        ticketKey: `ticket-${ticketId}`,
                    }),
                );
            } else {
                addTicketLogEntry(
                    'ticketId missing when answering SIP session',
                );
            }
        }

        if (rtcSessionRepresentsCentionErrand(session)) {
            //An email or some other means of communication that is not a phone call
            const deepLinkId = getCentionDataFromRtcSession(
                session,
                OMNICHANNEL_PROPERTIES.DEEP_LINK.value,
            );

            if (deepLinkId) {
                dispatch(
                    routeUserToOmnichannelErrand(deepLinkId, 'answerCall'),
                );
            } else {
                //There should never be a case where this happens
                addOmniChannelLogEntry(
                    'deepLinkId missing when answering omnichannel errand session',
                );
            }
        }

        /**
         * AC-811: Guard against double-dispatch of answerCall.
         * When a headset is connected, answering via the UI green button can cause
         * the headset SDK to also fire onAcceptCall, resulting in a second answerCall
         * dispatch. JsSIP's session.answer() only succeeds in STATUS_WAITING_FOR_ANSWER (4);
         * a second call after the status has moved to STATUS_ANSWERED (5) throws
         * INVALID_STATE_ERROR and skips addConnectionEventListeners, breaking audio.
         */
        if (!session.isInProgress() || session.isEstablished()) {
            addPhoneLogEntry(
                `Skipping answer for call ${session.data?.callId} — session already answered or not in progress (status: ${session.status})`,
            );
            return;
        }

        // Base the decision on the *actual* microphone permission, not the
        // cached app.micPermission Redux value. That value can get stuck at
        // `false` (e.g. after a transient USER_DENIED_MEDIA_ACCESS failure) with
        // nothing reliably clearing it, which previously blocked users from
        // answering calls even though the mic was permitted (AC-1484). This uses
        // a live check that also covers the macOS system-level permission in
        // the desktop app, same as the outgoing-call path.
        //
        // We only ever block on an *explicit* denial. This call runs while the
        // call is actively ringing, so we must not trigger a fresh blocking
        // getUserMedia prompt here (it could eat the ring window) — an
        // unresolved 'prompt' state falls through to answer() unchanged, same
        // as before this permission check existed; the browser will prompt
        // once the call's own media setup opens the mic. Wrapped in try/catch
        // so a failed check still fails OPEN, matching the
        // navigator.permissions.query behavior this replaced.
        try {
            const micPermissionState = await getMicrophonePermissionState();
            if (micPermissionState === permissionTypes.DENIED) {
                addPhoneLogEntry(
                    'NOTE: Mic permission denied -- not going through with answering the call',
                );
                dispatch(setMicPermission(false));
                return;
            }
            if (micPermissionState === permissionTypes.GRANTED) {
                dispatch(setMicPermission(true));
            }
        } catch (error) {
            addPhoneLogEntry(
                `NOTE: Could not check mic permission state, proceeding with answer: ${error}`,
            );
        }

        try {
            dispatch(logAudioDeviceDataIfAppropriate());
            session.answer(OPTIONS);
        } catch (error) {
            // This is a safeguard for not crashing the app in electron when answer is triggered multiple times
            // We know this happens when headset controls are active and we answer the call by clicking the green answer button in the
            // call canvas. We get an onAcceptCall which triggers an answerCall dispatch after having already dispatched answerCall.
            // TODO: WEB-14414- Fix this properly by not calling answerCall twice
            /*
             * AC-811: The isInProgress/isEstablished guard above now prevents the known
             * double-dispatch scenario from reaching this point. This catch block remains
             * as a last-resort safety net for any other unexpected JsSIP errors (e.g.
             * WebRTC failures, opaqueTransport errors) that could surface during
             * session.answer(). If you see this log line in production, investigate the
             * error — it likely indicates a new failure mode, not the old double-answer bug.
             */
            addPhoneLogEntry(`Failed to answer call: ${error}`);
            return;
        }
        session.data.answeredThroughHeadset = throughHeadset;
        addConnectionEventListeners(session, dispatch);
    };

const handleReceiveStream = (stream, session) => (dispatch, getState) => {
    const { phone, headsets } = getState();

    const audioReference = phone.get('audioReference');
    audioReference.srcObject = stream;

    const mediaDevices = headsets.get('connectedMediaDevices');
    const preferredAudioOutputId = headsets.get('preferredAudioOutputId');
    const preferredAudioOutput = getConnectedMediaDeviceOfKindWithId(
        mediaDevices,
        'audiooutput',
        preferredAudioOutputId,
    );

    const preferredAudioOuputLabel = preferredAudioOutput
        ? preferredAudioOutput.label
        : '';
    addDeviceLogEntry(
        'Media has preferred output device id: ' +
            preferredAudioOutputId +
            ' found: ' +
            !!preferredAudioOutput +
            ' name: ' +
            preferredAudioOuputLabel,
    );

    if (preferredAudioOutput) {
        setOutputDeviceWithIdToElement(
            preferredAudioOutput.deviceId,
            audioReference,
        );
    }

    dispatch(enableDtmf(stream, session));
};

const enableDtmf = (stream, session) => (dispatch) => {
    const peerConnection = session && session.connection;
    if (peerConnection) {
        const dtmfSender = createDtmfSender(peerConnection, stream);
        dispatch(registerDtmfSender(dtmfSender));
        session.data['DTMFSender'] = dtmfSender;
    }
};

const createDtmfSender = (peerConnection, stream) => {
    if (peerConnection.getSenders) {
        return peerConnection.getSenders()[0].dtmf;
    } else {
        // If the browser does not support .getSenders()
        // we fall back to use the deprecated .createDTMFSender().
        const audioTracks = stream.getAudioTracks();
        return peerConnection.createDTMFSender(audioTracks[0]);
    }
};

/** How long this surface shows its own intent before falling back to what the call says. */
const PENDING_MUTE_TIMEOUT_MS = 5000;

/**
 * Identifies one press, so that only that press's own timeout can retire it.
 *
 * The user may well mute and unmute in quick succession on purpose, and each press arms its own
 * timeout. Without a tag an earlier press's timeout clears a later press's intent, and the button
 * falls back to the call - which still carries the older state until the companion reports - so it
 * flips back under the user's finger.
 */
let pendingMuteToken = 0;

export const mutePhone =
    (mute = true) =>
    (dispatch, getState) => {
        const state = getState();

        // No media here, so the companion does the muting and reports back through TOP.
        if (shouldRelayToCompanion(state)) {
            const focusedActiveCall = getFocusedActiveCall(state);
            const focusedCallId = focusedActiveCall
                ? focusedActiveCall.get('callId')
                : null;
            muteCompanion(mute);
            dispatch({
                type: Actions.PHONE_MUTED,
                muted: mute,
                callId: focusedCallId,
            });
            // Until the companion's report arrives the call still carries the old state, so
            // show the intent instead. Cleared by an agreeing report, or by the timeout.
            pendingMuteToken += 1;
            const token = pendingMuteToken;
            dispatch({
                type: Actions.PHONE_MUTE_PENDING,
                muted: mute,
                token,
                // The call the intent is about. Reports arrive for every call this client can see,
                // including other extensions', and only one for this call can settle it.
                callKey: focusedActiveCall
                    ? focusedActiveCall.get('key')
                    : null,
            });
            setTimeout(() => {
                dispatch({ type: Actions.PHONE_MUTE_PENDING_CLEARED, token });
            }, PENDING_MUTE_TIMEOUT_MS);
            return;
        }

        const sessions = getLocalCallSessions(state);
        sessions.forEach((session) => {
            // Only what actually took effect is reported: other surfaces treat it as the
            // companion's real state, so a request that muted nothing must not travel as fact.
            // Still every request rather than every change - asking for a mute the endpoint is
            // already in applies cleanly and fires no session event, and a surface showing the
            // opposite would have nothing to correct it. Per session, so each names its channel.
            if (dispatch(muteCall(mute, session))) {
                dispatch(publishCompanionMuteState(mute, session));
            }
        });
    };

export const muteCall =
    (mute = true, session) =>
    (dispatch) => {
        dispatch({
            type: Actions.PHONE_MUTED,
            muted: mute,
            callId: session.data.callId,
        });
        // Attempting to mute a session without track puts it in a bad state
        if (!sessionHasAudioTracks(session)) {
            console.warn(
                `Attempted to ${
                    mute ? 'mute' : 'unmute'
                } a session without audio tracks`,
            );
            return false;
        }
        try {
            if (mute) {
                session.mute();
            } else {
                session.unmute();
            }
        } catch (error) {
            /**
             * When this occurrs, it's typically because the user has pressed
             * mute button on her headset while there's no call to mute.
             */
            const muteString = mute ? 'mute' : 'unmute';
            const infoMessage = `Attempted to ${muteString} but failed.`;
            const phoneLogEntry = `${infoMessage} Error: ${error}`;
            addPhoneLogEntry(phoneLogEntry);
            return false;
        }

        return true;
    };

export const hangupSession =
    (session, throughHeadset = false) =>
    (dispatch, getState) => {
        if (!session) {
            session = getFocusedSession(getState());
        }
        dispatch(putMeetingOnHold(false));
        if (!session || session.isEnded()) {
            return;
        }

        addPhoneLogEntry(
            'Hangup -> Attempting to stop locally generated tones',
        );
        stopTones();

        session.data.hungupThroughHeadset = throughHeadset;

        if (session.direction === 'incoming' && session.isInProgress()) {
            if (
                selectCompanionAnswersCalls(getState()) &&
                isRingingIncomingCall(session)
            ) {
                addUserPhoneLogEntry(
                    `Decline -> declining call ${session.data['callId']} on the desktop app too`,
                );
                declineCompanionCall(session.data['callId']);
            }
            if (getLocalCallIds(getState()).length > 1) {
                dispatch(
                    segmentTrack(EVENT_STRINGS.USER_DECLINED_SECOND_CALL, {
                        callId: session.data['callId'],
                    }),
                );
            }
            session.terminate(BUSY_TERMINATE_OPTIONS);
        } else {
            session.terminate();
        }
    };

export const setSoftphoneShouldBeRegistered =
    (shouldBeRegistered) => (dispatch) => {
        saveSoftphoneShouldBeRegistered(shouldBeRegistered);

        if (shouldBeRegistered) {
            dispatch(registerUserAgent());
        } else {
            dispatch(unregisterUserAgent());
        }
    };

export const setPlayRingtoneSetting = (playRingtoneSetting) => (dispatch) => {
    savePlayRingtoneSetting(playRingtoneSetting);
    dispatch(receivePlayRingtoneSetting(playRingtoneSetting));
};

export const loadPlayRingtoneSetting = () => (dispatch) => {
    const playRingtoneSetting = getPlayRingtoneSetting();
    dispatch(receivePlayRingtoneSetting(playRingtoneSetting));
};

export const receivePlayRingtoneSetting = (playRingtoneSetting) => ({
    type: Actions.PHONE_PLAY_RINGTONE_SETTING,
    playRingtoneSetting: playRingtoneSetting.key,
});

const registerUserAgent = () => (_dispatch, getState) => {
    const state = getState();
    const { phone } = state;

    if (phone.get('isRegistered')) {
        return;
    }

    if (
        selectPositionIsUnavailable(state) ||
        selectCallingNeedsCompanion(state)
    ) {
        addPhoneLogEntry(
            'Not registering the softphone: calling is blocked in this region',
        );
        return;
    }

    const userAgent = phone.get('userAgent');

    if (userAgent) {
        userAgent.register();
    }
};

const unregisterUserAgent = () => (_dispatch, getState) => {
    const { phone } = getState();
    const userAgent = phone.get('userAgent');
    if (userAgent) {
        userAgent.unregister();
    }
};

// Sends a SIP un-REGISTER and resolves once the registrar acknowledges it (the
// UA's 'unregistered' event), or immediately when there is no UserAgent. Used by
// the Electron main process on quit: it holds the quit until this resolves so
// the registration is removed while the renderer is still alive, instead of
// being left as a ghost (the renderer's own beforeunload can't reliably flush
// the un-REGISTER during teardown). The main process owns the single safety
// timeout that lets the app quit if the registrar never responds, so there is
// deliberately no timeout here.
export const unregisterUserAgentBeforeQuit = () => (_dispatch, getState) => {
    const userAgent = getState().phone.get('userAgent');
    if (!userAgent) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        userAgent.once('unregistered', () => resolve());
        userAgent.unregister();
    });
};

const registerDtmfSender = (dtmfSender) => ({
    type: Actions.PHONE_NEW_DTMF_SENDER,
    dtmf: dtmfSender,
});

const setPaiHeader = (pai) => ({ type: Actions.SET_PAI_HEADER, pai: pai });

const removePaiHeader = () => ({ type: Actions.REMOVE_PAI_HEADER });

export const registerPhoneAudioReference = (reference) => (dispatch) => {
    dispatch({ type: Actions.PHONE_AUDIO_REFERENCE, reference: reference });
};

export const transferCall = (activeCallDTO) => (dispatch, getState) => {
    const myExtensionKey = getOwnExtensionKey(getState());
    const activeCallKey = activeCallDTO.key;

    return new Promise((resolve, reject) => {
        ExtensionResource.updateActiveCall(
            {
                onResult: (/*json*/) => {
                    // Do nothing. We get back the same activeCallDTO as we sent.
                    resolve();
                },
                onError: () => {
                    networkErrorDispatcher(dispatch);
                    reject();
                },
                onClientError: () => {
                    reject();
                },
            },
            myExtensionKey,
            activeCallKey,
            activeCallDTO,
        );
    });
};

export const registerTransferPending =
    (callKey, extensionKey, number, contact) => (dispatch, getState) => {
        const session = getFocusedSession(getState());
        session?.unhold();

        dispatch({
            type: Actions.EXTENSION_ATTENDED_TRANSFER_PENDING,
            callKey,
            extensionKey,
            number,
            contact,
        });
    };

export const resolveTransferPending =
    (uniqueId, reason) => (dispatch, getState) => {
        // Since we can get 'AttendedTransferCancelled' events via top for any monitored extension,
        // we need to verify that it is for the current users transfer. This is done by comparing
        // the UniqueId in the cancel event to the key used to start the transfer. Since channels
        // can be renamed during a transfer, we compare against the "base" unique id.
        if (uniqueId) {
            const currentTransferCallKey =
                getPendingTransferCallKey(getState());
            if (!currentTransferCallKey) {
                return;
            }
            // e.g currentTransferCallKey = 'activecall-12345.1.2' => currentTransferBaseUniqueId = '12345'
            const currentTransferBaseUniqueId = /activecall-(\d+).*/.exec(
                currentTransferCallKey,
            )[1];
            // e.g uniqueId = '12345.1.2.3' => baseUniqueId = '12345
            const baseUniqueId = /(\d+).*/.exec(uniqueId)[1];

            if (currentTransferBaseUniqueId !== baseUniqueId) {
                return;
            }
        }

        const extensionKey = getOwnExtensionKey(getState());
        addUserPhoneLogEntry(
            `Attended transfer completed or cancelled, reason: ${reason}`,
        );
        dispatch({
            type: Actions.EXTENSION_ATTENDED_TRANSFER_RESOLVED,
            extensionKey,
        });
    };

export const playRingTone = () => (_dispatch, getState) => {
    const { phone, ringtones } = getState();
    const ringtoneEntityKey = ringtones.getIn(['settings', 'key']);
    const playRingtoneSetting = PlayRingtoneSetting.get(
        phone.get('playRingtoneSetting'),
    );

    if (playRingtoneSetting === PlayRingtoneSetting.SILENT) {
        addPhoneLogEntry(
            'Not playing Flow ringtone due to silent mode being set',
        );
        return;
    }

    if (selectCompanionAnswersCalls(getState())) {
        addPhoneLogEntry(
            'Not playing Flow ringtone, the desktop app rings for this call',
        );
        return;
    }

    // Catch error since we don't want the page to break if the browser is lacking support.
    try {
        addPhoneLogEntry(`Playing Flow ringtone with key ${ringtoneEntityKey}`);
        RingTonePlayer.play();
    } catch (error) {
        addPhoneLogEntry(
            `Failed playing Flow ringtone with key ${ringtoneEntityKey} due to error: ${error}`,
        );
        console.error(error);
    }
};

export const playRingbackTone = () => (_dispatch, getState) => {
    const { app, customer } = getState();
    const contact = app.getIn(['extension', 'contact']);
    const extensionKey = getOwnExtensionKey(getState());
    const loggedinCustomer = customer.get(extensionKey);
    const countryCode = getCountryCode(contact, loggedinCustomer);

    // Catch error since we don't want the page to break if the browser is lacking support.
    try {
        addPhoneLogEntry(
            `Playing progress tone for country code: ${countryCode}`,
        );
        ToneGenerator.playRingbackTone(countryCode);
    } catch (error) {
        addPhoneLogEntry(`Failed playing progress tone due to error: ${error}`);
        console.error(error);
    }
};

const playBusyTone = () => {
    try {
        addPhoneLogEntry('Playing locally generated busy tone');
        // It needs to be delayed to busy tone-not-playing-every-second-time.
        setTimeout(() => {
            ToneGenerator.playBusyTone();
        }, 0);
    } catch (error) {
        addPhoneLogEntry(`Failed playing busy tone due to error: ${error}`);
        console.error(`Failed to play busy tone ${error}`);
    }
};

const playDisconnectTone = () => {
    try {
        addPhoneLogEntry('Playing locally disconnect tone');
        // It needs to be delayed to play the tone after the call has ended.
        setTimeout(() => {
            ToneGenerator.playDisconnectTone();
        }, 0);
    } catch (error) {
        addPhoneLogEntry(
            `Failed playing locally generated disconnect tone due to error: ${error}`,
        );
        console.error(
            `Failed to play locally generated disconnect tone ${error}`,
        );
    }
};

const stopTones = (reasonString) => {
    // Catch error since we don't want the page to break if the browser is lacking support.
    try {
        const stoppingString = 'Stopping locally generated tones';
        const logString = reasonString
            ? `${stoppingString} because ${reasonString}`
            : stoppingString;
        addPhoneLogEntry(logString);
        ToneGenerator.stopRunningTone();
        RingTonePlayer.stop();
    } catch (error) {
        addPhoneLogEntry(
            `Failed stopping locally generated tones due to error: ${error}`,
        );
        console.error(error);
    }
};

/**
 * @param {string} number the destination
 * @param {string} [triggeredFrom] where the transfer was started, for the log
 * @param {string} [targetCallId] transfer this call rather than the focused one. See sendDTMF for
 *   why the companion has to name it: focus here is not evidence of which call the user meant.
 */
export const transferBlindCall =
    (number, triggeredFrom = 'unknown', targetCallId = null) =>
    (dispatch, getState) => {
        const state = getState();
        const targetActiveCall = targetCallId
            ? getOwnActiveCallByCallId(state, targetCallId)
            : getFocusedActiveCall(state);
        const activeCallKey = targetActiveCall
            ? targetActiveCall.get('key')
            : 'activecall-unknown';
        const isReturnBlindTransferActive = !!getBlindTransferSetting(
            BLIND_TRANSFER_SETTINGS.ACTIVE,
        );
        const returnDestination = isReturnBlindTransferActive
            ? getBlindTransferSetting(
                  BLIND_TRANSFER_SETTINGS.RETURN_DESTINATION,
              )
            : null;
        const returnDelay =
            isReturnBlindTransferActive && returnDestination
                ? getBlindTransferSetting(
                      BLIND_TRANSFER_SETTINGS.RETURN_DELAY,
                  ) || 15
                : null;

        const activeCallDTO = {
            key: activeCallKey,
            callTransfer: {
                destination: number,
                transferType: CallTransferType.blind.key,
                ...(returnDestination && { returnDestination }),
                ...(returnDelay && { returnDelay }),
            },
        };

        addUserPhoneLogEntry(
            `Activated blind transfer from location: ${triggeredFrom}\nBlind transfer settings:\n`,
            activeCallDTO,
        );

        dispatch(transferCall(activeCallDTO));
    };

export const transferAttendedCall =
    (number, transferContact = 'unknown', triggeredFrom = 'unknown') =>
    async (dispatch, getState) => {
        // Only one attended transfer can be pending at a time. Starting another
        // re-registers pending on the focused call and the backend rejects it
        // ("Already have a pending attended transfer"). The softphone and
        // extension-list transfer buttons already hide while one is pending
        // (CallAdaptiveButton returns null on this same signal), but other entry
        // points — keyboard shortcut, quick-transfer menu item, contact context
        // menu — dispatch here directly, so guard the shared action itself.
        if (getPendingTransferCallKey(getState())) {
            addUserPhoneLogEntry(
                `Ignored attended transfer from ${triggeredFrom} to ${number}: a transfer is already pending`,
            );
            dispatch(
                setSnackbar({
                    i18nextMessage: 'callcontrol.transfer_already_pending',
                }),
            );
            return;
        }

        const focusedActiveCall = getFocusedActiveCall(getState());
        const activeCallKey = focusedActiveCall
            ? focusedActiveCall.get('key')
            : '';
        const ownExtensionKey = getOwnExtensionKey(getState());

        const activeCallDTO = {
            key: activeCallKey,
            callTransfer: {
                destination: number,
                transferType: CallTransferType.attended.key,
                attendedTransferState: AttendedTransferState.pending.key,
            },
        };

        addUserPhoneLogEntry(
            `Activated attended transfer from location: ${triggeredFrom}\nAttended transfer to: ${number}`,
        );

        dispatch(
            registerTransferPending(
                activeCallKey,
                ownExtensionKey,
                number,
                transferContact,
            ),
        );
        try {
            await dispatch(transferCall(activeCallDTO));
        } catch (_error) {
            dispatch(
                resolveTransferPending(undefined, 'start transfer failed'),
            );
        }
    };

export const completeAttendedTransfer = () => async (dispatch, getState) => {
    const state = getState();

    const activeCallDTO = {
        key: getPendingTransferCallKey(state),
        callTransfer: {
            destination: getPendingTransferNumber(state),
            transferType: CallTransferType.attended.key,
            attendedTransferState: AttendedTransferState.complete.key,
        },
    };

    try {
        await dispatch(transferCall(activeCallDTO));
    } catch (_error) {
        addUserPhoneLogEntry(
            'Error when user tried to complete the attended transfer',
        );
        return;
    }

    dispatch(
        resolveTransferPending(
            undefined,
            'transfer complete by user interaction',
        ),
    );
};

export const cancelAttendedTransfer = () => async (dispatch, getState) => {
    const state = getState();

    const activeCallDTO = {
        key: getPendingTransferCallKey(state),
        callTransfer: {
            destination: getPendingTransferNumber(state),
            transferType: CallTransferType.attended.key,
            attendedTransferState: AttendedTransferState.cancel.key,
        },
    };

    try {
        await dispatch(transferCall(activeCallDTO));
    } catch (_error) {
        addUserPhoneLogEntry(
            'Error when user tried to cancel the attended transfer',
        );
        return;
    }

    dispatch(
        resolveTransferPending(
            undefined,
            'transfer cancelled by user interaction',
        ),
    );
};

export const toggleCallWaiting = (callWaitingDTO) => (dispatch, getState) => {
    const myExtensionKey = getOwnExtensionKey(getState());
    const callWaiting = callWaitingDTO.get('callWaiting');

    dispatch({ type: Actions.SET_CALL_WAITING, callWaiting: callWaitingDTO });

    dispatch(
        segmentTrack(EVENT_STRINGS.USER_TOGGLED_CALL_WAITING, {
            isEnabled: callWaiting,
        }),
    );

    ExtensionResource.setCallWaiting(
        { onResult() {}, onError: networkErrorDispatcher(dispatch) },
        myExtensionKey,
        callWaitingDTO.toJS(),
    );

    addUserPhoneLogEntry('Call waiting toggled ' + callWaiting ? 'on' : 'off');
};

export const swapCall = (callId) => (dispatch, getState) => {
    const state = getState();
    dispatch(
        segmentTrack(EVENT_STRINGS.USER_SWAPPED_CALLS, { callId: callId }),
    );
    const sessions = getLocalCallSessions(state);
    if (selectMeetingIsActive(state)) {
        if (selectMeetingIsInFocus(state)) {
            dispatch(putMeetingOnHold(true));
        } else {
            dispatch(putMeetingOnHold(false));
        }
    }
    sessions.forEach((session) => {
        if (session.data['callId'] === callId) {
            session.unhold();
        } else {
            session.hold();
        }
    });
};

/**
 * Batch DTMF for the companion. Every keypress is its own action, so relaying each one separately
 * meant an HTTP round-trip and a TOP push per digit, with no ordering guarantee between them -
 * typing a nine-digit IVR selection sent nine commands. Digits are buffered here and flushed as one
 * command once the user stops pressing.
 *
 * The local tone is still played per keypress by the caller, so the keypad stays responsive; only
 * the network command is delayed. The companion hands the whole string to RTCDTMFSender, which
 * applies its own inter-tone gap.
 */
const COMPANION_DTMF_FLUSH_MS = 400;
// Bound the buffer so a stuck key cannot grow it without limit before the idle flush fires.
const COMPANION_DTMF_MAX_DIGITS = 32;
let companionDtmfDigits = '';
let companionDtmfCallId = null;
let companionDtmfTimer = null;

const flushCompanionDtmf = () => {
    if (companionDtmfTimer) {
        clearTimeout(companionDtmfTimer);
        companionDtmfTimer = null;
    }
    if (!companionDtmfDigits || !companionDtmfCallId) {
        return;
    }
    const digits = companionDtmfDigits;
    const callId = companionDtmfCallId;
    companionDtmfDigits = '';
    companionDtmfCallId = null;
    sendCompanionDtmf(digits, callId);
};

const bufferCompanionDtmf = (symbol, callId) => {
    // Switching call mid-burst must not merge digits across calls.
    if (companionDtmfCallId && companionDtmfCallId !== callId) {
        flushCompanionDtmf();
    }
    companionDtmfCallId = callId;
    companionDtmfDigits += symbol;

    if (companionDtmfDigits.length >= COMPANION_DTMF_MAX_DIGITS) {
        flushCompanionDtmf();
        return;
    }
    if (companionDtmfTimer) {
        clearTimeout(companionDtmfTimer);
    }
    companionDtmfTimer = setTimeout(
        flushCompanionDtmf,
        COMPANION_DTMF_FLUSH_MS,
    );
};

/**
 * @param {string|number} symbol the digit(s) to send
 * @param {string} [targetCallId] send on this call rather than the focused one. The companion
 *   passes it because the call a relayed command names is not necessarily the one in focus here -
 *   the user is pressing keys on another machine, so this client's focus says nothing about which
 *   call they meant, and with two calls up the tones would otherwise land in the wrong one.
 */
export const sendDTMF =
    (symbol, targetCallId = null, playLocalTone = true) =>
    (_dispatch, getState) => {
        const state = getState();

        // Model A: the tone has to be injected into the RTP stream, which lives on the companion.
        // Play it locally too so the keypad still feels responsive to the user pressing it.
        if (shouldRelayToCompanion(state)) {
            const focusedActiveCall = getFocusedActiveCall(state);
            const remoteCallId = focusedActiveCall
                ? focusedActiveCall.get('callId')
                : null;
            if (!remoteCallId) {
                addUserPhoneLogEntry(
                    'DTMF in companion mode with no focused call, ignoring',
                );
                return;
            }
            if (typeof symbol == 'number' || symbol.length === 1) {
                ToneGenerator.playDigit(symbol);
            }
            bufferCompanionDtmf(String(symbol), remoteCallId);
            return;
        }

        const localCallId = targetCallId || getFocusedLocalCallId(state);
        const targetSession = getLocalSessionFromCallId(state, localCallId);
        const dtmfSender = targetSession && targetSession.data['DTMFSender'];
        if (senderSupportsDtmf(dtmfSender)) {
            // Suppressed when the keypress happened somewhere else. The companion runs in the
            // background and the user is pressing keys on another surface, which plays its own
            // feedback tone - and with the CTI and the companion on one machine, playing it here
            // too means the user hears every digit twice. The tone that matters is the one
            // insertDTMF puts in the RTP stream; this one is only UI feedback.
            if (
                playLocalTone &&
                (typeof symbol == 'number' || symbol.length === 1)
            ) {
                ToneGenerator.playDigit(symbol);
            }
            dtmfSender.insertDTMF(symbol);
        }
    };

export const toggleFocusedCall = (dispatch, getState) => {
    const state = getState();
    const focusedActiveCall = getFocusedActiveCall(state);
    if (focusedActiveCall) {
        dispatch(toggleCall(focusedActiveCall.get('key')));
    }
};

export const getPinCode =
    (callKey, meetingCode) => async (dispatch, getState) => {
        let result;
        try {
            if (meetingCode) {
                const fetchUrl = homeActionUrlPath(
                    `/api/external/conference/${meetingCode}/pin`,
                );
                const res = await fetch(fetchUrl, { method: 'post' });
                result = await res.json();
            }
        } catch (e) {
            addUserPhoneLogEntry(
                `Error fetching call key for meetingcode ${meetingCode}:`,
                e,
            );
        }
        if (result) {
            const pin = result.pin;
            const extensionKey = getOwnExtensionKey(getState());
            await dispatch(setPinCode(extensionKey, callKey, pin));
        }
        return result;
    };
